import { IBoardSnapshot } from '../../../dtos/IBoardSnapshot';
import { TConnectionStatus } from '../logic/BoardConnection';
import { BANNER_MS } from '../components/RoundBanner';
import { TAKEOVER_MS } from '../components/PhaseTakeover';
import {
  EFFECTS_VISIBLE_MS,
  ROTATE_EVERY_MS,
} from '../components/RankingBoard';
import DemoTournament from './DemoTournament';

/** O que um passo da demo pode fazer com o telão. */
export interface IDemoControls {
  model: () => DemoTournament;
  emit: (snapshot: IBoardSnapshot) => void;
  setStatus: (status: TConnectionStatus) => void;
  reset: () => void;
  later: (fn: () => void, ms: number) => void;
}

export interface IDemoStep {
  id: string;
  title: string;
  /** O que olhar na tela durante o passo. */
  detail: string;
  /** Quanto tempo o tour fica neste passo. */
  holdMs: number;
  run: (ctl: IDemoControls) => void;
}

const APPLY_MS = BANNER_MS + EFFECTS_VISIBLE_MS;

/**
 * Garante o torneio ao vivo antes de um passo clicado fora de ordem. Se for
 * preciso abrir o torneio, espera a abertura de fase sair da frente.
 */
function withLive(ctl: IDemoControls, fn: () => void) {
  if (ctl.model().isCompleted) {
    ctl.reset();
    ctl.later(() => ctl.emit(ctl.model().start()), 600);
    ctl.later(fn, 600 + TAKEOVER_MS + 400);
    return;
  }
  if (!ctl.model().isLive) {
    ctl.emit(ctl.model().start());
    ctl.later(fn, TAKEOVER_MS + 400);
    return;
  }
  fn();
}

export const DEMO_STEPS: IDemoStep[] = [
  {
    id: 'before',
    title: 'Aguardando início',
    detail:
      'Mascote da FRL, contagem regressiva em que só o dígito que muda rola, cronograma das próximas rodadas com a arena de cada uma e QR code para o público acompanhar no celular.',
    holdMs: 8000,
    run: ctl => ctl.reset(),
  },
  {
    id: 'start',
    title: 'O torneio começou',
    detail:
      'Abertura em tela cheia com o logo, título que encolhe até o lugar e confete nas cores da marca. As duas arenas aparecem no painel "Agora", cada uma com a equipe em quadra.',
    holdMs: TAKEOVER_MS + 2500,
    run: ctl => {
      if (ctl.model().isLive || ctl.model().isCompleted) {
        ctl.reset();
        ctl.later(() => ctl.emit(ctl.model().start()), 1500);
      } else {
        ctl.emit(ctl.model().start());
      }
    },
  },
  {
    id: 'round',
    title: 'Rodada encerrada',
    detail:
      'A tela escurece, o cartão da equipe chega com mola, a pontuação conta do zero e a arena aparece no rodapé do cartão. Depois a tabela aplica: brilho atravessando a linha, número contando, chip "+N" subindo e a arena da rodada ao lado da escola.',
    holdMs: APPLY_MS + 1500,
    run: ctl => withLive(ctl, () => ctl.emit(ctl.model().closeRound())),
  },
  {
    id: 'overtake',
    title: 'Ultrapassagem e novo líder',
    detail:
      'A última colocada dispara: a coluna pula para o grupo dela, a linha sobe deslizando até o 1º lugar, as outras caem uma posição e o novo líder pulsa em ouro com confete.',
    holdMs: APPLY_MS + 2000,
    run: ctl => withLive(ctl, () => ctl.emit(ctl.model().overtake())),
  },
  {
    id: 'simultaneous',
    title: 'Duas arenas ao mesmo tempo',
    detail:
      'Os banners entram em fila, um por vez, cada um com a sua arena. A tabela só muda depois do último, já com o estado mais novo — o público vê o resultado antes da classificação.',
    holdMs: 2 * BANNER_MS + EFFECTS_VISIBLE_MS + 1500,
    run: ctl => withLive(ctl, () => ctl.emit(ctl.model().closeSimultaneous())),
  },
  {
    id: 'startNext',
    title: 'Rodada começou',
    detail:
      'Sem banner (horário não é suspense): a equipe passa de "Próximas" para "Agora" com um anel de brilho e o selo COMEÇOU!',
    holdMs: 7000,
    run: ctl => withLive(ctl, () => ctl.emit(ctl.model().startNext())),
  },
  {
    id: 'break',
    title: 'Intervalo',
    detail:
      'O intervalo em andamento aparece no topo do painel "Agora", junto das rodadas que continuam.',
    holdMs: 6000,
    run: ctl => withLive(ctl, () => ctl.emit(ctl.model().startBreak())),
  },
  {
    id: 'offline',
    title: 'Conexão instável',
    detail:
      'Se a rede da TV cair, o rodapé troca "ao vivo" por "reconectando…" em vermelho. Nada some da tela; quando volta, a última versão é aplicada.',
    holdMs: 7500,
    run: ctl => {
      ctl.setStatus('offline');
      ctl.later(() => ctl.setStatus('live'), 5500);
    },
  },
  {
    id: 'rotate',
    title: 'Troca de página',
    detail: `Com mais grupos do que cabem, a classificação alterna a cada ${
      ROTATE_EVERY_MS / 1000
    }s. A pílula laranja enche até a próxima troca e para durante banners e efeitos.`,
    holdMs: ROTATE_EVERY_MS + 3000,
    run: ctl => withLive(ctl, () => undefined),
  },
  {
    id: 'finish',
    title: 'Resultado final',
    detail:
      'Abertura "Resultado final" com a campeã de cada grupo entrando em sequência. Depois a classificação fica fixa como resultado oficial.',
    holdMs: TAKEOVER_MS + 3000,
    run: ctl => withLive(ctl, () => ctl.emit(ctl.model().finish())),
  },
];
